import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import Text from 'v3/components/Text';
import api from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import { useValidation, useModal } from 'hooks';
import { parseErrors } from 'v3/utils/formatter';
import moment from 'moment';
import SwitchOption from 'v3/components/SwitchOption';
import SharedData from '../shared/Section';
import Inputs from '../shared/Data/index';
import Editable from '../shared/Editable';
import { useShippingCompany } from '../shared/context';
import { toApiDate } from 'v3/utils/rntrcValidation';
import { formatSimplesNacional } from 'v3/pages/ShippingCompany/Requests/shared/formatters';
import { dataSchema } from '../shared/validator';
import Modal from '../shared/Modals/PaymentMethod';

export default function Data() {
  const shippingCompany = useShippingCompany();
  const snackbar = useSnackbar();
  const [isEditing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, { validate }] = useValidation(dataSchema, {
    pathPrefix: 'data.',
  });
  const [active, setActive] = useState(!shippingCompany.deleted_at);
  const isCompany = shippingCompany?.data?.personType?.name === 'Jurídica';
  const isDriver = !!shippingCompany?.data?.is_driver;
  const paymentMethod = useModal();

  useEffect(() => {
    shippingCompany.setErrors(errors);
  }, [errors]);

  useEffect(() => {
    if (shippingCompany.data.deleted_at) {
      setActive(false);
    } else setActive(true);
  }, [shippingCompany]);

  async function saveData() {
    setLoading(true);

    try {
      const data = {
        type_id: shippingCompany.data.personType.name === 'Jurídica' ? 3 : 2,
        shipment_link_id: shippingCompany.data?.shipment_link?.id || null,
        cgccpf: shippingCompany.data.cgccpf,
        ie: shippingCompany.data.ie || null,
        social_name: shippingCompany.data.social_name,
        fantasy_name: shippingCompany.data.fantasy_name || null,
        phone: shippingCompany.data.phone,
        phone_unmasked: shippingCompany.data.phone.replace(/[^0-9]+/g, ''),
        email: shippingCompany.data.email,
        is_driver: shippingCompany.data.is_driver || null,
        rntrc_number: shippingCompany?.data?.rntrc_number || null,
        rntrc_issue_date: toApiDate(shippingCompany.data?.rntrc_issue_date),
        rntrc_exp_date: toApiDate(shippingCompany.data?.rntrc_exp_date),
        rntrc_type: shippingCompany.data?.rntrc_type?.name ?? null,
      };

      // Simples Nacional e data de início só vão quando mudaram: enviar o campo cria a linha em
      // carg_shipping_companies para transportadora legada, e ausente significa "não altera".
      const simplesNacional = shippingCompany.data?.simples_nacional ?? null;
      if (simplesNacional !== (shippingCompany.prevShippingCompany?.simples_nacional ?? null)) {
        data.simples_nacional = simplesNacional;
      }
      const emissionStartDate = toApiDate(shippingCompany.data?.emission_start_date);
      if (
        emissionStartDate !== toApiDate(shippingCompany.prevShippingCompany?.emission_start_date)
      ) {
        data.emission_start_date = emissionStartDate;
      }

      await api.put(`v3/shipping-company/${shippingCompany.data.id}/data`, {
        data,
      });

      shippingCompany.setPrevShippingCompany(shippingCompany.data);
      snackbar.show(<Text>Salvo com sucesso</Text>, { type: 'success' });
      setLoading(false);
      setEditing(false);
      shippingCompany.setErrors({});
    } catch (error) {
      let message = '';
      if (error.response) {
        const parsedErrors = parseErrors(error.response.data, {
          getPath: (e) => e.field,
        });
        shippingCompany.setErrors(parsedErrors);
        message = error.response?.data.message;
      }

      snackbar.show(<Text>{message || 'Erro ao salvar'}</Text>, {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    setLoading(true);
    const normalizeData = (data) => ({
      personType: data?.personType?.name || '',
      cgccpf: data?.cgccpf || '',
      pispasep: data?.pispasep || '',
      ie: data?.ie || '',
      fantasy_name: data?.fantasy_name || '',
      social_name: data?.social_name || '',
      rntrc_number: data?.rntrc_number || '',
      rntrc_issue_date: data?.rntrc_issue_date || '',
      rntrc_exp_date: data?.rntrc_exp_date || '',
      rntrc_type: data?.rntrc_type?.name || '',
      is_driver: !!data?.is_driver,
      shipment_link: data?.shipment_link?.id || '',
      phone: data?.phone || '',
      email: data?.email || '',
      simples_nacional: data?.simples_nacional ?? null,
      emission_start_date: toApiDate(data?.emission_start_date),
    });

    const currentData = normalizeData(shippingCompany.data);
    const previousData = normalizeData(shippingCompany.prevShippingCompany);

    if (JSON.stringify(currentData) === JSON.stringify(previousData)) {
      snackbar.show(<Text>Salvo com sucesso</Text>, { type: 'success' });
      setEditing(false);
      setLoading(false);
      shippingCompany.setErrors({});
      return;
    }

    const [isValid] = await validate(shippingCompany.data);

    if (!isValid) {
      snackbar.show(<Text>Verifique os campos obrigatórios</Text>, {
        type: 'error',
      });
      setLoading(false);
      return;
    }

    if (!shippingCompany?.data?.shippingCompanies?.payment?.id && shippingCompany.data.is_driver) {
      paymentMethod.open();
    } else {
      saveData();
    }
  }

  async function toggleActive() {
    try {
      await api.put(`/persons/${shippingCompany.data.id}`, {
        cgccpf: shippingCompany.data.cgccpf,
        deleted_at: active ? moment() : null,
      });
      shippingCompany.data.deleted_at = active ? moment() : null;
      setActive((old) => !old);
      shippingCompany.setPrevShippingCompany(shippingCompany.data);
    } catch {
      snackbar.show(<Text> Erro ao alterar transportadora! </Text>, {
        type: 'error',
      });
    }
  }

  return (
    <SharedData
      Title="Dados da transportadora"
      HeaderRightComponent={
        <Editable
          isEditing={isEditing}
          setEditing={setEditing}
          onSave={handleSubmit}
          isLoading={loading}
        />
      }
      loading={shippingCompany.isFetching}
    >
      <Modal
        isOpen={paymentMethod.isOpen}
        onClose={paymentMethod.close}
        saveData={saveData}
        setUpperLoading={setLoading}
      />
      {isEditing ? (
        <Inputs />
      ) : (
        <Row>
          <Col xs={12} md={12} as="p">
            <SwitchOption value={active} onClick={() => toggleActive()} />
          </Col>
          <Col xs={12}>
            <Text type="label" color="dark">
              Tipo de Pessoa
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany.data?.personType?.name || 'Não informado'}
            </Text>
          </Col>
          <Col xs={6} md={6}>
            <Text type="label" color="dark">
              {isCompany ? 'CNPJ' : 'CPF'}
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.cgccpf || 'Não informado'}
            </Text>
          </Col>
          {!isCompany ? (
            <Col xs={6} md={6}>
              <Text type="label" color="dark">
                Pis/Pasep
              </Text>
              <Text type="regular" color="gray" as="p" className="mt-2">
                {shippingCompany?.data?.pispasep || 'Não informado'}
              </Text>
            </Col>
          ) : null}
          {isCompany ? (
            <Col xs={12} md={6}>
              <Text type="label" color="dark">
                Inscrição Estadual
              </Text>
              <Text type="regular" color="gray" as="p" className="mt-2">
                {shippingCompany?.data?.ie || 'Não informado'}
              </Text>
            </Col>
          ) : null}
          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              {isCompany ? 'Razão Social' : 'Nome'}
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.social_name || 'Não informado'}
            </Text>
          </Col>
          {isCompany ? (
            <Col xs={12} md={6}>
              <Text type="label" color="dark">
                Nome fantasia
              </Text>
              <Text type="regular" color="gray" as="p" className="mt-2">
                {shippingCompany?.data?.fantasy_name || 'Não informado'}
              </Text>
            </Col>
          ) : null}
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              RNTRC
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.rntrc_number || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              Tipo de RNTRC
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.rntrc_type?.name || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              Data de emissão RNTRC
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.rntrc_issue_date || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              Validade RNTRC
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.rntrc_exp_date || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              É motorista?
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {isDriver ? 'Sim' : 'Não'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              Nível de serviço
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.shippingCompanies?.description_service_level ||
                'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              Optante Simples Nacional
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {formatSimplesNacional(shippingCompany?.data?.simples_nacional)}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              Início emissão contra CT-e
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.emission_start_date || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              Telefone
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.phone || 'Não informado'}
            </Text>
          </Col>
          <Col md={6} xs={12}>
            <Text type="label" color="dark">
              Vinculo ao transportador
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.shipment_link?.name || 'Não informado'}
            </Text>
          </Col>
          <Col xs={12}>
            <Text type="label" color="dark">
              E-mail
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {shippingCompany?.data?.email || 'Não informado'}
            </Text>
          </Col>
        </Row>
      )}
    </SharedData>
  );
}
